import { ComparePageTemplate, type CompareContent } from './ComparePageTemplate';

const content: CompareContent = {
  competitor: 'Greenhouse',
  competitorSegment: 'Enterprise ATS (founded 2012)',
  tagline:
    'Everything Greenhouse charges a six-figure add-on for — AI interviews, explainable scoring, agency mode — ships natively in GoGrabJob Business at a fraction of the price.',
  intro:
    'Greenhouse is a respected name in enterprise recruiting, built on the principle of structured hiring. But "structured" has come to mean expensive customisation, months-long implementations, and a growing list of paid add-ons for the capabilities modern teams actually need. GoGrabJob Business was designed from the ground up to make those capabilities default, not premium.',

  tableGroups: [
    {
      groupName: 'General',
      rows: [
        { feature: 'Free trial available',        ours: 'yes',          theirs: 'no'      },
        { feature: 'Transparent public pricing',  ours: 'yes',          theirs: 'no'      },
        { feature: 'Starting monthly price',      ours: 'Free → ₹999',  theirs: '$6,000+/yr' },
        { feature: 'Setup & go-live time',        ours: '< 48 hours',   theirs: '4–8 weeks' },
        { feature: 'India / INR pricing',         ours: 'yes',          theirs: 'no'      },
        { feature: 'Mobile-friendly web app',     ours: 'yes',          theirs: 'partial' },
      ],
    },
    {
      groupName: 'Core ATS Capabilities',
      rows: [
        { feature: 'Applicant tracking system',       ours: 'yes',         theirs: 'yes'     },
        { feature: 'Job posting & career page',       ours: 'yes',         theirs: 'yes'     },
        { feature: 'Structured interview kits',       ours: 'yes',         theirs: 'yes'     },
        { feature: 'Visual pipeline / kanban',        ours: 'yes',         theirs: 'partial' },
        { feature: 'Built-in email sequences',        ours: 'yes',         theirs: 'partial' },
        { feature: 'Candidate self-scheduling',       ours: 'yes',         theirs: 'add-on'  },
        { feature: 'Offer management & approvals',    ours: 'yes',         theirs: 'yes'     },
        { feature: 'Multi-location / multi-division', ours: 'yes',         theirs: 'yes'     },
      ],
    },
    {
      groupName: 'AI & Automation',
      rows: [
        { feature: 'AI video + voice interviews',     ours: 'yes',               theirs: 'no'      },
        { feature: 'Explainable role-fit scoring',    ours: 'Gap-level analysis', theirs: 'no'      },
        { feature: 'Automated candidate ranking',     ours: 'yes',               theirs: 'add-on'  },
        { feature: 'Multilingual AI (EN/HI)',         ours: 'yes',               theirs: 'no'      },
        { feature: 'Visual workflow automation',      ours: 'yes',               theirs: 'partial' },
        { feature: 'Resume parsing (AI)',             ours: 'yes',               theirs: 'yes'     },
        { feature: 'Bias reduction controls',         ours: 'yes',               theirs: 'partial' },
      ],
    },
    {
      groupName: 'Agency & Multi-Client',
      rows: [
        { feature: 'Agency client mode',              ours: 'yes',  theirs: 'no'  },
        { feature: 'Branded submission packs',        ours: 'yes',  theirs: 'no'  },
        { feature: 'Client-scoped pipelines',         ours: 'yes',  theirs: 'no'  },
        { feature: 'Multi-account management',        ours: 'yes',  theirs: 'no'  },
      ],
    },
    {
      groupName: 'Reporting & Compliance',
      rows: [
        { feature: 'EEOC / DEI reporting',       ours: 'yes',  theirs: 'yes'     },
        { feature: 'Full audit trail',            ours: 'yes',  theirs: 'yes'     },
        { feature: 'GDPR tooling & DPA',          ours: 'yes',  theirs: 'yes'     },
        { feature: 'SSO (SAML / OIDC)',           ours: 'yes',  theirs: 'yes'     },
        { feature: 'Custom analytics dashboards', ours: 'yes',  theirs: 'add-on'  },
      ],
    },
    {
      groupName: 'Support & Integrations',
      rows: [
        { feature: 'Live chat support',           ours: 'yes',   theirs: 'no'      },
        { feature: 'Dedicated onboarding',        ours: 'yes',   theirs: 'yes'     },
        { feature: 'API access',                  ours: 'yes',   theirs: 'yes'     },
        { feature: 'Native HRIS integrations',    ours: '20+',   theirs: '600+'    },
        { feature: 'LinkedIn integration',        ours: 'yes',   theirs: 'yes'     },
      ],
    },
  ],

  metrics: [
    { value: '10×',   label: 'Lower total cost',      sub: 'vs Greenhouse enterprise pricing' },
    { value: '48 hr', label: 'Time to first live role', sub: 'vs 4–8 weeks for Greenhouse' },
    { value: '3×',    label: 'More placements',        sub: 'per recruiter per month on AI-assisted pipeline' },
  ],

  deepDive: [
    {
      title: 'Greenhouse charges extra for what GoGrabJob ships by default',
      body: 'Greenhouse customers routinely pay $15,000–$25,000 per year for the base ATS, then stack on HireVue for video interviews ($35,000+/yr), Gem for sourcing automation ($8,000+/yr), and Lever or Calendly for self-scheduling. GoGrabJob Business bundles AI video and voice interviews, role-fit scoring with gap analysis, email sequences, and workflow automation into every paid plan. The difference in total cost of ownership over three years is significant — often exceeding $100,000 for a mid-size team.',
      highlight: 'GoGrabJob Business includes AI interviews, scoring, and workflow automation in the base plan — no add-ons required.',
    },
    {
      title: 'Implementation that takes hours, not weeks',
      body: 'A typical Greenhouse enterprise rollout involves 4–8 weeks of configuration, data migration, SSO setup, and IT onboarding. GoGrabJob Business has been designed for fast activation: create an account, connect your email and calendar, configure your first role, and run your first AI interview — all within 48 hours. For startups and growing teams, the opportunity cost of a slow ATS launch is measured in missed hires. For agencies, it is lost revenue.',
      highlight: 'Customers report running their first AI interview within 2 days of signing up — no implementation project required.',
    },
    {
      title: 'Explainable AI scoring vs "black box" recommendations',
      body: "Greenhouse's AI features are primarily resume ranking without transparent scoring logic. GoGrabJob Business produces a role-fit score for every candidate that breaks down matched skills, skill gaps, and weighted importance — a scorecard you can present to any hiring manager or client without embarrassment. When a decision gets challenged, you can point to evidence: the rubric, the criteria weighting, and the candidate's verbatim responses.",
    },
    {
      title: 'Built for agencies — not just in-house teams',
      body: "Greenhouse was architected for in-house corporate recruiting. Agencies using Greenhouse rely on workarounds: creating separate organisations per client, using tags as client identifiers, or stitching together spreadsheets. GoGrabJob Business has a native agency client mode with separate pipelines per client, client-scoped access for hiring managers, and branded submission packs. If you run a multi-client recruitment business, there is no workaround needed.",
    },
    {
      title: 'India and APAC — actually supported, not an afterthought',
      body: 'Greenhouse is priced in USD and built primarily for the US and Western European markets. GoGrabJob Business offers INR pricing that makes it accessible to Indian startups, SMEs, and recruitment agencies. Our AI supports English, Hindi, and Hinglish interviews out of the box — covering the communication styles actually used in Indian workplaces. Data residency in ap-south-1 (Mumbai) means lower latency and simpler compliance.',
    },
  ],

  ourAdvantages: [
    'AI video + voice interviews built-in at no extra cost',
    'Explainable gap-level role-fit scoring your HMs can trust',
    'Native agency client mode with branded submission packs',
    'Live in 48 hours — no months-long implementation',
    'INR pricing — accessible for startups, SMEs, and Indian agencies',
    'Multilingual AI: English, Hindi, and Hinglish supported',
    'Visual workflow automation included in all paid plans',
    'Live chat support — not just email tickets',
  ],

  theirAdvantages: [
    'Larger integration marketplace with 600+ pre-built connectors',
    'Stronger enterprise compliance workflows and approval chains',
    'Deeper CRM and talent pool nurture tooling',
    'Broader name recognition in enterprise procurement processes',
    'More mature EEO/OFCCP compliance reporting for US-regulated firms',
  ],

  faqs: [
    {
      q: 'Can I import our existing data from Greenhouse into GoGrabJob?',
      a: 'Yes. GoGrabJob Business provides a data import wizard that accepts CSV exports from Greenhouse for jobs, candidates, and interview notes. We also offer a supported migration service for larger accounts — your customer success manager will guide the process to ensure nothing is lost.',
    },
    {
      q: 'Does GoGrabJob integrate with the HRIS systems we already use?',
      a: 'GoGrabJob Business offers native integrations with Workday, BambooHR, Keka, Darwinbox, and more via our HRIS connector library. A REST API and webhook system are available for custom integrations. Our API surface closely mirrors standard HR data schemas so existing integrations port over quickly.',
    },
    {
      q: 'How does the AI interview compare to solutions like HireVue?',
      a: 'GoGrabJob AI interviews are purpose-built for structured hiring assessment, not facial analysis. Questions are mapped to your job-specific competency rubric; every answer is scored against those criteria and presented with supporting evidence from the candidate\'s verbatim response. This makes scoring auditable, explainable, and far less susceptible to legal challenge than sentiment or affect-based scoring models.',
    },
    {
      q: 'What does GoGrabJob cost compared to Greenhouse?',
      a: 'GoGrabJob Business has a free Starter plan for teams with up to 1 active role. Paid plans begin at ₹999/month (or equivalent in USD/GBP/EUR). Greenhouse is not publicly priced but typically starts at $6,000/year for small teams and scales to $25,000+ for enterprise. A fair comparison for a 10-person recruiting team shows GoGrabJob saving 80–90% on software spend while adding capabilities Greenhouse requires add-ons to match.',
    },
    {
      q: 'Is GoGrabJob Business compliant with GDPR and Indian data protection requirements?',
      a: 'Yes. GoGrabJob Business provides a GDPR-compliant Data Processing Agreement for all paid customers, Standard Contractual Clauses for EEA data transfers, and built-in data subject request workflows. We are also aligned to the India Digital Personal Data Protection Act 2023 (DPDPA). Data residency in Mumbai (ap-south-1) is the default; EEA residency (Ireland) is available for Enterprise customers.',
    },
  ],
};

export function VsGreenhousePage() {
  return <ComparePageTemplate content={content} />;
}
